import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import {
  faWhatsapp,
  faInstagram,
  faTelegram,
  faLinkedin,
} from "@fortawesome/free-brands-svg-icons";

import enFlag from "../locale/en/en.webp";
import ptFlag from "../locale/pt/pt.webp";

function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activePage, setActivePage] = useState("");
  const { i18n, t } = useTranslation();
  const currentLanguage = i18n.language;
  var navbarItems = t("Home.Navbar", { returnObjects: true });

  var iconsMap = {
    faPhone: faPhone,
    faEnvelope: faEnvelope,
    faWhatsapp: faWhatsapp,
    faInstagram: faInstagram,
    faTelegram: faTelegram,
    faLinkedin: faLinkedin,
  };

  useEffect(() => {
    const savedLanguage = localStorage.getItem("language");
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  const changeLanguage = () => {
    const newLanguage = currentLanguage === "en" ? "pt" : "en";
    i18n.changeLanguage(newLanguage);
    localStorage.setItem("language", newLanguage);
  };

  useEffect(() => {
    const currentPath = location.pathname + location.hash; // Inclui o hash
    setActivePage(currentPath === "" ? "" : currentPath);
  }, [location]);

  const handleLinkClick = (link, e) => {
    if (e) {
      e.preventDefault();
    }

    if (link.url && link.url.match(/^(http|mailto:|tel:)/)) {
      window.open(link.url, "_blank");
      return;
    }

    if (link.path === "/") {
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }, 100);
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      setActivePage(link.path);
      return;
    }

    if (link.path === "/tools") {
      if (location.pathname !== "/tools") {
        navigate("/tools");
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }, 100);
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      setActivePage(link.path);
      return;
    }

    if (link.path.includes("#")) {
      const [basePath, sectionId] = link.path.split("#");

      // Se a rota atual já for a mesma, apenas faça scroll suave com offset
      if (location.pathname === basePath) {
        const element = document.getElementById(sectionId);
        if (element) {
          const y = element.getBoundingClientRect().top + window.pageYOffset - 40;
          window.scrollTo({ top: y, behavior: "smooth" });
          setActivePage(link.path);
        }
      } else {
        // Caso a rota seja diferente, navega primeiro e então faz o scroll com offset
        navigate(basePath); // Navega até a página base

        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            const y = element.getBoundingClientRect().top + window.pageYOffset - 40;
            window.scrollTo({ top: y, behavior: "smooth" });
          }
          setActivePage(link.path); // Atualiza o estado de página ativa
        }, 100); // Pequeno atraso para garantir que o DOM tenha sido atualizado
      }
      return;
    } else {
      // Para links sem hash, apenas navegue
      navigate(link.path);
      setActivePage(link.path);
    }
  };

  return (
    <nav className="navbar navbar-expand-lg fixed-top home-index">
      <div className="container-fluid">
        <button
          className="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#HomeNavbar"
          aria-controls="HomeNavbar"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>
        <div className="collapse navbar-collapse" id="HomeNavbar">
          <ul className="navbar-nav">
            {Object.keys(navbarItems).map((key) => {
              const link = navbarItems[key];

              return (
                <li
                  key={key}
                  className={`nav-item${link.submenu ? " dropdown" : ""}`}
                >
                  <Link
                    className={`nav-link${
                      link.submenu ? " dropdown-toggle" : ""
                    } ${activePage === link.path ? "active" : ""}`}
                    to={link.path}
                    onClick={(e) => handleLinkClick(link, e)}
                    {...(link.submenu && {
                      role: "button",
                      "data-bs-toggle": "dropdown",
                      "aria-expanded": "false",
                    })}
                  >
                    {link.label}
                    <div className="box-line">
                      <div className="line"></div>
                    </div>
                  </Link>
                  {link.submenu && (
                    <ul className="dropdown-menu">
                      {Object.keys(link.submenu).map((key) => {
                        const className = key.toLowerCase();
                        const submenu = link.submenu[key];
                        return (
                          <li
                            key={key}
                            className={`dropdown-link ${className}`}
                          >
                            {submenu.url.match(/^(http|mailto:|tel:)/) ? (
                              // Link externo
                              <a
                                href={submenu.url}
                                onClick={(e) => handleLinkClick(submenu, e)}
                                className="dropdown-item"
                              >
                                <FontAwesomeIcon
                                  icon={iconsMap[submenu.icon]}
                                  size="lg"
                                />
                                {key}
                              </a>
                            ) : (
                              // Link interno
                              <Link
                                to={submenu.url}
                                onClick={(e) => handleLinkClick(submenu, e)}
                                className="dropdown-item"
                              >
                                <FontAwesomeIcon
                                  icon={iconsMap[submenu.icon]}
                                  size="lg"
                                />
                                {key}
                              </Link>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
          <div onClick={changeLanguage} className="lang">
            <img
              src={currentLanguage === "en" ? ptFlag : enFlag}
              alt={currentLanguage === "en" ? "Português" : "English"}
              style={{ width: "30px" }}
            />
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
